/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
  UseMutationResult
} from "react-query";
import { useDispatch } from "react-redux";

import { ApiResponse } from "../../interfaces/Api";
import { ILeaveType } from "../../interfaces/LeaveInterface";
import { notificationAdd } from "../../actions/notification";
import useCurrentResourceCentre from "../useCurrentResourceCentre";
import { getLeaveTypes, postLeaveType, patchLeaveType, deleteLeaveType } from "../../api/leaveType";
import { leaveBalancesQueryKey } from "./useLeaveBalances";

export const leaveTypesQueryKey = "leaveTypes";

// use leave types
export const useLeaveTypes = (): UseQueryResult<Array<ILeaveType>, Error> => {
  const resourceCentre = useCurrentResourceCentre();

  return useQuery({
    queryKey: [leaveTypesQueryKey, resourceCentre?.id],
    queryFn: async () => {
      if (!resourceCentre?.id) return [];

      const response = await getLeaveTypes({
        resourceCentreId: Number(resourceCentre.id)
      });

      return response.data || [];
    },
    enabled: !!resourceCentre?.id
  });
};

// use post leave type
export const usePostLeaveType = (): UseMutationResult<
  ApiResponse<ILeaveType>,
  Error,
  Partial<ILeaveType>,
  unknown
> => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<ILeaveType>) => postLeaveType(data),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: [leaveTypesQueryKey] });
        queryClient.invalidateQueries({ queryKey: [leaveBalancesQueryKey] });
        dispatch(
          notificationAdd({
            id: Date.now(),
            autoTimeout: true,
            message: "Leave type created successfully",
            variant: "success"
          })
        );
      }
    },
    onError: (error: any) => {
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: error?.message || "Failed to create leave type",
          variant: "error"
        })
      );
    }
  });
};

// use patch leave type
export const usePatchLeaveType = (): UseMutationResult<
  ApiResponse<ILeaveType>,
  Error,
  { id: number | string; data: Partial<ILeaveType> },
  unknown
> => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => patchLeaveType({ id, data }),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: [leaveTypesQueryKey] });
        queryClient.invalidateQueries({ queryKey: [leaveBalancesQueryKey] });
        dispatch(
          notificationAdd({
            id: Date.now(),
            autoTimeout: true,
            message: "Leave type updated successfully",
            variant: "success"
          })
        );
      }
    },
    onError: (error: any) => {
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: error?.message || "Failed to update leave type",
          variant: "error"
        })
      );
    }
  });
};

// use delete leave type
export const useDeleteLeaveType = (): UseMutationResult<
  ApiResponse<null>,
  Error,
  number | string,
  unknown
> => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => deleteLeaveType(id),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: [leaveTypesQueryKey] });
        queryClient.invalidateQueries({ queryKey: [leaveBalancesQueryKey] });
        dispatch(
          notificationAdd({
            id: Date.now(),
            autoTimeout: true,
            message: "Leave type deleted successfully",
            variant: "success"
          })
        );
      }
    },
    onError: (error: any) => {
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: error?.message || "Failed to delete leave type",
          variant: "error"
        })
      );
    }
  });
};
